// 🔎♥ QUICK FIND REFERENCE — saved merchants with one-tap deep links to their apps/deals.
import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, X } from "lucide-react";
import { dealUrlFor, brandDomain } from "@/lib/brandLogos";
import { feedCapacitor } from "@/lib/capacitor";
import { useConsumerAuth } from "@/consumer/ConsumerAuth";
import { IdentityBadge, BadgeStudio } from "@/components/IdentityBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const WalletReceipts = () => {
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/consumer/points`, { withCredentials: true })
      .then((r) => setEvents((r.data.events || []).slice(0, 8))).catch(() => setEvents(null));
  }, []);
  if (!events || events.length === 0) return null;
  return (
    <div data-testid="wallet-receipts" className="mt-2 rounded-xl bg-slate-800/40 border border-slate-700/50">
      <button data-testid="wallet-receipts-toggle" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
        <span className="text-base">🧾</span>
        <span className="flex-1 font-heading font-black text-[12px] tracking-wider text-slate-300">RECEIPTS — recent POG BUCKS</span>
        <span className="text-slate-500 text-xs font-black">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-3 pb-2.5 space-y-1.5">
          {events.map((e, i) => (
            <div key={i} data-testid={`receipt-row-${i}`} title={`${e.reason} · ${e.ts || ""}`} className="flex items-center gap-2 text-[11px] border-t border-slate-700/40 pt-1.5">
              <span className={`shrink-0 font-mono font-black ${e.amount >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {e.amount >= 0 ? "+" : ""}{e.amount}
              </span>
              <span className="flex-1 min-w-0 text-slate-300 truncate">{e.reason}</span>
              <span className="shrink-0 text-slate-500">{(e.ts || "").slice(5, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WalletIdCard = () => {
  const { driver, refresh } = useConsumerAuth() || {};
  const [open, setOpen] = useState(false);
  if (!driver) return null;
  const dname = driver.pog_nickname || driver.name || "POG'R";
  return (
    <>
      <button data-testid="wallet-id-badge" onClick={() => setOpen(true)}
        className="mt-3 w-full flex items-center gap-3 rounded-xl bg-gradient-to-r from-sky-500/15 to-cyan-500/10 border border-sky-400/40 px-3 py-2.5 hover:border-sky-300/70 transition-colors text-left">
        <IdentityBadge uid={driver.user_id} name={dname} size={44}
          ver={localStorage.getItem("airpog_badge_ver") || undefined} showFlair />
        <span className="flex-1 min-w-0">
          <span className="block font-heading font-black text-[14px] text-white truncate">{dname}</span>
          <span data-testid="wallet-id-tier" className="block text-[10px] font-black tracking-wider mt-0.5">
            {driver.is_plus
              ? <span className="text-amber-300">👑 AIR.POG PLUS</span>
              : <span className="text-slate-400">FREE POG'R</span>}
          </span>
        </span>
        <span className="shrink-0 text-[10px] font-black tracking-wider text-sky-300/80">🪪 EDIT ID →</span>
      </button>
      <BadgeStudio open={open} onClose={() => setOpen(false)}
        uid={driver.user_id} name={dname} ghost={!!driver.leaderboard_hidden} onSaved={refresh} />
    </>
  );
};

const WalletBalance = () => {
  const [bal, setBal] = useState(null);
  useEffect(() => {
    axios.get(`${API}/tickets/me`, { withCredentials: true })
      .then((r) => setBal({ bucks: r.data.pog_bucks || 0, wheel: r.data.tickets || 0, golden: r.data.golden_tickets || 0 }))
      .catch(() => setBal(null));
  }, []);
  if (!bal) return null;
  return (
    <div data-testid="wallet-balance" className="mt-3 rounded-xl bg-gradient-to-r from-amber-500/15 to-yellow-500/10 border border-amber-400/40 px-3 py-2.5">
      <a href="/market" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
        <span className="text-lg">🪙</span>
        <span data-testid="wallet-bucks" className="flex-1 min-w-0 font-heading font-black text-[15px] text-amber-300">{bal.bucks.toLocaleString()} POG BUCKS</span>
        <span className="shrink-0 text-[10px] font-black tracking-wider text-amber-400/80">POG MARKET 🛒 →</span>
      </a>
      <a href="/tickets" className="mt-1.5 flex items-center gap-2 border-t border-amber-400/20 pt-1.5 hover:opacity-80 transition-opacity">
        <span className="text-lg">🎡</span>
        <span data-testid="wallet-wheel-tickets" className="flex-1 min-w-0 font-heading font-black text-[13px] text-cyan-200">{bal.wheel.toLocaleString()} POG WHEEL TICKET{bal.wheel === 1 ? "" : "S"}</span>
        <span className="shrink-0 text-[10px] font-black tracking-wider text-cyan-300/80">SPIN →</span>
      </a>
      <a href="/tickets" className="mt-1.5 flex items-center gap-2 border-t border-amber-400/20 pt-1.5 hover:opacity-80 transition-opacity">
        <span className="text-lg">🎟</span>
        <span data-testid="wallet-tickets" className="font-heading font-black text-[13px] text-yellow-200">{bal.golden.toLocaleString()} AIR.POG GOLDEN TICKET{bal.golden === 1 ? "" : "S"}</span>
      </a>
    </div>
  );
};

export const QuickFindMerchants = () => {
  const [favs, setFavs] = useState(null);
  const [name, setName] = useState("");
  const [web, setWeb] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [searching, setSearching] = useState(false);
  const load = () => axios.get(`${API}/consumer/favorites`, { withCredentials: true })
    .then((r) => setFavs(r.data.favorites || [])).catch(() => setFavs([]));
  useEffect(() => { load(); }, []);

  // 🔎🌍 MERCHANT FINDER — live suggestions for ANY merchant on Earth (app 🔥 first, web 🌐 second)
  useEffect(() => {
    const q = name.trim();
    if (q.length < 2) { setSugs([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      axios.get(`${API}/merchant-finder`, { params: { q } })
        .then((r) => setSugs(r.data.results || []))
        .catch(() => setSugs([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(t);
  }, [name]);

  // 🔥 WALLET SELF-HEAL — light the APP button for saves that landed web-only
  const [enrich, setEnrich] = useState({});
  useEffect(() => {
    if (!favs || !favs.length) return;
    const names = [...new Set(favs.filter((f) => !f.app_url && (f.name || "").trim().length >= 2).map((f) => f.name.trim()))].slice(0, 40);
    if (!names.length) return;
    axios.post(`${API}/merchant-finder/apps`, { names })
      .then((r) => setEnrich((m) => ({ ...m, ...(r.data.apps || {}) })))
      .catch(() => {});
  }, [favs]);

  // 🔥 MOST-SAVED BY POG'RS — community hot list
  const [topSaves, setTopSaves] = useState([]);
  useEffect(() => {
    axios.get(`${API}/wallet/top-saves`).then((r) => setTopSaves(r.data.top || [])).catch(() => {});
  }, []);

  const trackTap = (nm) => { if (nm) axios.post(`${API}/wallet/tap`, { names: [nm] }).catch(() => {}); feedCapacitor(); };

  const saveSuggestion = async (s) => {
    const dom = (s.web || "").replace(/^https?:\/\//, "").split("/")[0].replace(/^www\./, "").toLowerCase();
    const id = `manual_${(dom || s.name.toLowerCase().replace(/[^a-z0-9]+/g, "_")).slice(0, 60)}`;
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/consumer/favorites/${encodeURIComponent(id)}`,
        { name: s.name, web: s.web || "", app_url: s.app_url || "", logo: s.logo || "" }, { withCredentials: true });
      setName(""); setWeb(""); setSugs([]);
      load();
    } catch (e) {
      setMsg(e?.response?.status === 401
        ? "Sign in on the map first — then your saved merchants follow you everywhere ♥"
        : e?.response?.data?.detail || "Couldn't save — try again!");
    }
    setBusy(false);
  };

  const addMerchant = async () => {
    const n = name.trim();
    if (!n) return;
    const dom = brandDomain(n) || (web.trim().replace(/^https?:\/\//, "").split("/")[0] || "").toLowerCase();
    const id = `manual_${(dom || n.toLowerCase().replace(/[^a-z0-9]+/g, "_")).slice(0, 60)}`;
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/consumer/favorites/${encodeURIComponent(id)}`, { name: n, web: web.trim() }, { withCredentials: true });
      setName(""); setWeb("");
      load();
    } catch (e) {
      setMsg(e?.response?.status === 401
        ? "Sign in on the map first — then your saved merchants follow you everywhere ♥"
        : e?.response?.data?.detail || "Couldn't save — try again!");
    }
    setBusy(false);
  };

  const unfav = (id) => axios.post(`${API}/consumer/favorites/${encodeURIComponent(id)}`, {}, { withCredentials: true })
    .then(() => setFavs((f) => f.filter((x) => x.id !== id))).catch(() => {});

  if (!favs) return null;
  return (
    <div className="glass rounded-2xl p-4" data-testid="quick-find-merchants">
      <p className="font-heading font-extrabold text-sm text-rose-300 flex items-center gap-1.5">
        <Heart className="w-4 h-4 fill-rose-400 text-rose-400" /> POG'KET WALLET ♥ · QUICK FIND
      </p>
      <WalletIdCard />
      <WalletBalance />
      <WalletReceipts />
      {/* ➕ Add ANY merchant — online-only shops welcome, no map pin needed */}
      <div className="mt-3 rounded-xl bg-slate-800/40 border border-slate-700/50 p-2.5" data-testid="qf-add-form">
        {topSaves.length > 0 && (
          <div data-testid="qf-top-saves" className="mb-2.5">
            <p className="text-[10px] font-black tracking-widest text-amber-300/90 mb-1.5">🔥 MOST-SAVED BY POG'RS</p>
            <div className="relative">
              <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1 pr-6">
              {topSaves.map((t, i) => (
                <button key={i} data-testid={`qf-top-${i}`} onClick={() => saveSuggestion(t)} disabled={busy}
                  className="shrink-0 flex items-center gap-1.5 rounded-full pl-1 pr-2.5 py-1 bg-slate-800/70 border border-slate-600/60 hover:border-amber-400/50 disabled:opacity-50">
                  <span className="w-6 h-6 rounded-full bg-white overflow-hidden flex items-center justify-center">
                    {t.logo ? <img src={t.logo} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="text-[11px]">🏪</span>}
                  </span>
                  <span className="text-[11px] font-heading font-bold text-white max-w-[110px] truncate">{t.name}</span>
                  <span className="text-[9px] font-black text-amber-300">♥{t.count}</span>
                  {t.trend === "up" && <span className="text-[9px] font-black text-emerald-300">▲</span>}
                  {t.trend === "down" && <span className="text-[9px] font-black text-rose-400">▼</span>}
                  {t.trend === "new" && <span className="text-[8px] font-black text-amber-300 bg-amber-400/15 rounded px-1">NEW</span>}
                  {t.opens > 0 && <span className="text-[8px] font-black text-sky-300">{t.opens}↗wk</span>}
                </button>
              ))}
              </div>
              <span className="pointer-events-none absolute right-0 top-0 bottom-1 w-8 bg-gradient-to-l from-slate-900 to-transparent" />
            </div>
          </div>
        )}
        <p className="text-[10px] font-black tracking-widest text-slate-400">🔎 SEARCH & SAVE ANY MERCHANT ON EARTH — apps 🔥 & websites 🌐</p>
        <div className="flex gap-1.5 mt-2">
          <input data-testid="qf-add-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
            onKeyDown={(e) => e.key === "Enter" && addMerchant()}
            placeholder="Merchant name (e.g. SHEIN)"
            className="flex-1 min-w-0 rounded-lg bg-slate-900 border border-slate-700 px-2.5 py-2 text-[12px] text-white placeholder:text-slate-500 outline-none focus:border-rose-400/60" />
          <button data-testid="qf-add-btn" onClick={addMerchant} disabled={busy || !name.trim()}
            className="shrink-0 rounded-lg bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-white text-[11px] font-black px-3">
            {busy ? "…" : "SAVE ♥"}
          </button>
        </div>
        {(searching || sugs.length > 0) && (
          <div data-testid="qf-suggestions" className="mt-1.5 rounded-lg bg-slate-900/90 border border-slate-700 overflow-hidden">
            {searching && sugs.length === 0 && (
              <p className="px-2.5 py-2 text-[11px] text-slate-400 font-bold">🔎 Scanning the merchant-verse…</p>
            )}
            {sugs.map((s, i) => (
              <button key={i} data-testid={`qf-sug-${i}`} onClick={() => saveSuggestion(s)} disabled={busy}
                className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-slate-800/80 border-t border-slate-800 first:border-t-0 disabled:opacity-50">
                <span className="w-8 h-8 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {s.logo ? <img src={s.logo} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span>🏪</span>}
                </span>
                <span className="flex-1 min-w-0 font-heading font-bold text-[12px] text-white truncate">{s.name}</span>
                {s.has_app && <span className="shrink-0 rounded-full bg-orange-500/15 border border-orange-400/40 text-orange-300 text-[9px] font-black px-1.5 py-0.5">APP 🔥</span>}
                {s.web && <span className="shrink-0 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 text-[9px] font-black px-1.5 py-0.5">WEB 🌐</span>}
                <span className="shrink-0 text-rose-300 text-[10px] font-black">SAVE ♥</span>
              </button>
            ))}
          </div>
        )}
        <input data-testid="qf-add-web" value={web} onChange={(e) => setWeb(e.target.value)} maxLength={200}
          onKeyDown={(e) => e.key === "Enter" && addMerchant()}
          placeholder="Website (optional — helps us find their app & logo)"
          className="w-full mt-1.5 rounded-lg bg-slate-900 border border-slate-700 px-2.5 py-2 text-[12px] text-white placeholder:text-slate-500 outline-none focus:border-rose-400/60" />
        {msg && <p data-testid="qf-add-msg" className="mt-1.5 text-[11px] font-bold text-amber-300">{msg}</p>}
      </div>
      {favs.length === 0 ? (
        <p className="text-[12px] text-slate-400 mt-2">Tap the ♥ on any map card to save a merchant here — then jump straight to their deals anytime.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {favs.map((f) => {
            const dom = brandDomain(f.name || "") || (f.web ? (f.web.replace(/^https?:\/\//, "").split("/")[0] || "").toLowerCase() : "");
            const dealUrl = dealUrlFor({ name: f.name, links: f.web ? { web: f.web } : undefined }) || f.web || null;
            const found = enrich[(f.name || "").trim().toLowerCase()];
            const appUrl = f.app_url || (found && found.app_url) || "";
            const logoUrl = f.logo || (found && found.logo) || "";
            return (
              <div key={f.id} data-testid={`qf-row-${f.id}`} className="flex items-center gap-2.5 bg-slate-800/50 border border-slate-700/60 rounded-xl px-2.5 py-2">
                <span className="w-9 h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { if (dom && !e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = `${BACKEND}/api/brand-logo/${dom}?v=3`; e.currentTarget.className = "w-7 h-7 object-contain"; } else { e.currentTarget.style.display = "none"; } }} />
                    : dom ? <img src={`${BACKEND}/api/brand-logo/${dom}?v=3`} alt="" className="w-7 h-7 object-contain" loading="lazy" /> : <span>🏪</span>}
                </span>
                <p className="flex-1 min-w-0 font-heading font-bold text-[13px] text-white truncate">{f.name || "Saved merchant"}</p>
                {appUrl && (
                  <a data-testid={`qf-app-${f.id}`} href={appUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackTap(f.name)}
                    className="shrink-0 rounded-full bg-orange-500/15 border border-orange-400/40 text-orange-300 text-[10px] font-black px-2.5 py-1.5 hover:bg-orange-500/25">
                    APP 🔥
                  </a>
                )}
                {dealUrl && (
                  <a data-testid={`qf-deals-${f.id}`} href={dealUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackTap(f.name)}
                    className={`shrink-0 rounded-full text-[10px] font-black px-2.5 py-1.5 ${appUrl
                      ? "bg-sky-500/15 border border-sky-400/40 text-sky-300 hover:bg-sky-500/25"
                      : "bg-orange-500/15 border border-orange-400/40 text-orange-300 hover:bg-orange-500/25"}`}>
                    {appUrl ? "WEB 🌐" : "🔥 Deals ↗"}
                  </a>
                )}
                <button data-testid={`qf-remove-${f.id}`} onClick={() => unfav(f.id)} title="Remove"
                  className="shrink-0 w-6 h-6 rounded-full bg-slate-700/60 hover:bg-slate-600 flex items-center justify-center">
                  <X className="w-3 h-3 text-slate-400" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
